import { type SiteStep, Step2Answers, Step7Answers, type StepStatus } from '@tabel/shared/schemas/sites';
import { stepReady } from '@tabel/shared/sites/step-texts';
import type { StepState } from '../../../components/ui';

/*
 * Чистые правила страницы площадки (007 T040): какой шаг открыть, вид кружка, чтение ответов шагов и времени. Решения
 * о статусах принимает сервер (`checks.ts`); здесь — только показ того, что он прислал.
 */

/** Статус шага сервера → состояние кружка `Steps` (дизайн-система Steps). */
export const STEP_STATE: Readonly<Record<StepStatus, StepState>> = {
  todo: 'todo',
  current: 'cur',
  wait: 'wait',
  ok: 'ok',
  bad: 'bad',
  warn: 'warn',
};

/** Шаги по очереди (FR-011): 1 → 3 → 4 → 5 → 6, затем 9 и 10. Шаги 2 и 7 — в любой момент, 8 — по желанию. */
const MAIN = [1, 3, 4, 5, 6, 9, 10] as const;
const ANY_TIME = [2, 7] as const;

const locked = (s: Pick<SiteStep, 'code'>) => s.code === 'LOCKED';

/**
 * Текущий шаг — открыт при входе на страницу (FR-010, ux «Steps»: «Открыт текущий шаг»): первый не готовый и не
 * закрытый шаг очереди, потом шаги «в любой момент». Всё готово — `null`.
 */
export function currentStep(steps: readonly SiteStep[]): number | null {
  for (const n of [...MAIN, ...ANY_TIME]) {
    const s = steps.find((x) => x.step === n);
    if (s && !stepReady(s) && !locked(s) && s.status !== 'todo') return n;
  }
  return null;
}

/** Номер шага из адреса `#step6` (ссылка для телефона, шаг 6). */
export function stepFromHash(hash: string): number | null {
  const m = /^#step(\d{1,2})$/.exec(hash);
  const n = m ? Number(m[1]) : Number.NaN;
  return n >= 1 && n <= 10 ? n : null;
}

/** Якорь шага на странице: `step6`. */
export const stepAnchor = (n: number) => `step${n}`;

/** Ответы шага 2 из ответа сервера; не по форме — `null` (поля пустые). */
export function step2Answers(s: SiteStep | undefined) {
  const r = Step2Answers.safeParse(s?.answers);
  return r.success ? r.data : null;
}

/** Времена выгрузки шага 7: из ответа или по умолчанию. */
export function step7Times(s: SiteStep | undefined, fallback: readonly string[]): string[] {
  const r = Step7Answers.safeParse(s?.answers);
  return r.success ? r.data.times : [...fallback];
}

/** Нажатия шага 6 (`{ inAt, outAt }`, research R19). */
export function controlPresses(s: SiteStep | undefined): { inAt: string | null; outAt: string | null } {
  const a = s?.answers ?? {};
  const text = (v: unknown) => (typeof v === 'string' && v !== '' ? v : null);
  return { inAt: text(a.inAt), outAt: text(a.outAt) };
}

/** Найденная отметка контрольного прохода (детали шага 6, `checks.ts`). */
export interface FoundPass {
  kind: 'in' | 'out';
  pressedAt: string;
  passAt: string | null;
  ip: string | null;
  direction: 'IN' | 'OUT' | null;
  delaySec: number | null;
}

/** Строки таблицы шага 6: найденные отметки и ненайденная (`CTL_NOT_FOUND`: нажатие без отметки). */
export function controlRows(s: SiteStep | undefined): FoundPass[] {
  const d = s?.details ?? {};
  const list = Array.isArray(d.found) ? d.found : [];
  const rows: FoundPass[] = [];
  for (const x of list) {
    if (!x || typeof x !== 'object') continue;
    const o = x as Record<string, unknown>;
    if ((o.kind !== 'in' && o.kind !== 'out') || typeof o.pressedAt !== 'string') continue;
    rows.push({
      kind: o.kind,
      pressedAt: o.pressedAt,
      passAt: typeof o.passAt === 'string' ? o.passAt : null,
      ip: typeof o.ip === 'string' ? o.ip : null,
      direction: o.direction === 'IN' || o.direction === 'OUT' ? o.direction : null,
      delaySec: typeof o.delaySec === 'number' ? o.delaySec : null,
    });
  }
  if (s?.code === 'CTL_NOT_FOUND' && (d.kind === 'in' || d.kind === 'out')) {
    const pressed = controlPresses(s)[d.kind === 'in' ? 'inAt' : 'outAt'];
    if (pressed && !rows.some((r) => r.kind === d.kind))
      rows.push({
        kind: d.kind,
        pressedAt: pressed,
        passAt: null,
        ip: null,
        direction: null,
        delaySec: null,
      });
  }
  return rows;
}

/** Пункты наблюдения (детали шага 9, `checks.ts`). */
export interface ObservationItems {
  night: boolean;
  extract: boolean;
  terminals: boolean;
  clean: boolean;
  days: number;
}

export function observationItems(s: SiteStep | undefined): ObservationItems {
  const raw = s?.details?.items;
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    night: o.night === true,
    extract: o.extract === true,
    terminals: o.terminals === true,
    clean: o.clean === true,
    days: typeof o.days === 'number' ? o.days : 0,
  };
}

/** Факты строк пунктов наблюдения (детали шага 9, `checks.ts`, T049): чего нет в деталях — `null` или 0. */
export interface ObservationFacts {
  firstPassAt: string | null;
  lastExtract: { at: string; rows: number } | null;
  /** Шаг 7 подтверждён вручную: пункт выгрузки засчитан этим подтверждением. */
  extractManualAt: string | null;
  terminalsSeen: number;
  terminalsTotal: number;
}

export function observationFacts(s: SiteStep | undefined): ObservationFacts {
  const d = s?.details ?? {};
  const time = (v: unknown) => (typeof v === 'string' && Number.isFinite(Date.parse(v)) ? v : null);
  const count = (v: unknown) => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null);
  const e =
    d.lastExtract && typeof d.lastExtract === 'object' ? (d.lastExtract as Record<string, unknown>) : null;
  const extractAt = time(e?.at);
  const rows = count(e?.rows);
  return {
    firstPassAt: time(d.firstPassAt),
    lastExtract: extractAt !== null && rows !== null ? { at: extractAt, rows } : null,
    extractManualAt: time(d.extractManualAt),
    terminalsSeen: count(d.terminalsSeen) ?? 0,
    terminalsTotal: count(d.terminalsTotal) ?? 0,
  };
}

function offsetMs(tz: string): number {
  const m = /^([+-])(\d{2}):(\d{2})$/.exec(tz);
  if (!m) return 0;
  const v = (Number(m[2]) * 60 + Number(m[3])) * 60_000;
  return m[1] === '-' ? -v : v;
}

/**
 * Дата и время «дд.мм.гггг чч:мм:сс» по поясу площадки (контрольный проход: нажатие и отметка). С датой: проверку могли
 * пройти вчера, а в таблице даты абсолютные (ux.md «Таблица»).
 */
export function localClock(iso: string, tz: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const local = new Date(t + offsetMs(tz)).toISOString();
  return `${local.slice(8, 10)}.${local.slice(5, 7)}.${local.slice(0, 4)} ${local.slice(11, 19)}`;
}

/** Дата «дд.мм» из `YYYY-MM-DD` (итог дня наблюдения: «20.10: 388 отметок…»). */
export function dayMonth(isoDate: string): string {
  return `${isoDate.slice(8, 10)}.${isoDate.slice(5, 7)}`;
}
